// Survivor pictures (CHANGELOG 8) and outfits (docs/OUTFITI_garderoba.md). Gear never changes
// the picture; an outfit unlocked in the Collection book and chosen on the Appearance screen does.
//   full figure: 1024 on desktop, 512 on phones
//   portrait:    256 (profile, report, share card), 128 (HUD, lists, encounters)
// Every big figure stands in `FigurePanel`: the dark gradient and the warm glow the Survivor screen
// had before the wardrobe. Common, Rare and Epic outfits (and every outfit in small views, with
// reduced motion or Save-Data) are transparent pictures on it. A Legendary outfit on a big view is a
// looping video that already holds that background and the glow (5.2, 5.3): it fills the panel's
// height, and the panel's own glow is left out.

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { CHARACTER_ASPECT, bodyTypeOf, characterImage, characterPortrait, oppPortrait, outfitAnimUrls, outfitAnimated, outfitPortraitUrl, outfitUrl, type BodyType, type OutfitRef, type PortraitSize, type SetId } from '@lastday/shared';
import { cx } from '@/lib/format';
import { NpcPortrait } from './Art';

const DESKTOP = '(min-width: 1024px)';

/** A missing outfit file shows the base look; in dev it also says which file (2). */
function missing(src: string) {
  if (import.meta.env.DEV) console.warn(`[outfit] could not load ${src}; showing the base look`);
}

/** Reduced motion or Save-Data: pictures only, no video (5.2). */
function stillNow(): boolean {
  if (typeof window === 'undefined') return true;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const save = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
  return reduce || save;
}

function useStillOnly(): boolean {
  const [still, setStill] = useState(stillNow);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq?.addEventListener) return;
    const on = () => setStill(stillNow());
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return still;
}

/**
 * The figure panel's background, as the Survivor screen had it before the wardrobe. A picture: it
 * keeps its warm colours under the Crimson palette (1. 10. 2026), and so does the glow.
 */
export const PANEL_BG = 'linear-gradient(180deg,#1A1712 0%,#2E2117 70%,#3A2718 100%)';
/** The warm glow behind the figure: 180 px at 38% of the height (smaller in small panels). Part of the picture. */
export const FIGURE_GLOW = 'radial-gradient(circle, rgba(232,163,61,0.22) 0%, rgba(232,163,61,0) 70%)';
/** The softer glow behind a figure without its panel (the look cards when a survivor is made). */
export const FIGURE_GLOW_SOFT = 'radial-gradient(ellipse at 50% 70%, rgba(232,163,61,0.16) 0%, rgba(232,163,61,0) 65%)';
/** The Legendary videos are 560 × 752 (3:4). */
const VIDEO_ASPECT = 560 / 752;
/** Soft left and right edges (about 4% of the width), so the video meets the panel gradient unseen. */
const VIDEO_EDGE = 'linear-gradient(to right, transparent 0, black 4%, black 96%, transparent 100%)';

const FIGURE_CLS = 'block h-full w-auto max-w-full select-none object-contain';

/** The Legendary loop: muted and inline (iOS), paused off screen and in a background tab. */
function OutfitVideo({ bodyType, set, alt, className }: { bodyType: BodyType; set: SetId; alt: string; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const urls = outfitAnimUrls(bodyType, set);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // React sets `muted` as a property only; iOS wants the attribute before it plays by itself.
    v.muted = true;
    v.setAttribute('muted', '');
    let visible = true;
    const sync = () => {
      if (visible && !document.hidden) v.play().catch(() => {});
      else v.pause();
    };
    const io = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(([e]) => ((visible = e.isIntersecting), sync()), { threshold: 0.05 }) : null;
    io?.observe(v);
    document.addEventListener('visibilitychange', sync);
    return () => {
      io?.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, [failed, urls.webm]);
  const box: CSSProperties = { aspectRatio: VIDEO_ASPECT, maskImage: VIDEO_EDGE, WebkitMaskImage: VIDEO_EDGE };
  const cls = cx('block h-full w-auto max-w-none select-none object-contain', className);
  // A video that cannot load keeps its first frame, quietly (5.2).
  if (failed) return <img src={urls.poster} alt={alt} draggable={false} data-outfit-poster="" className={cls} style={box} />;
  return (
    <video
      ref={ref}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      poster={urls.poster}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      data-outfit-video=""
      className={cls}
      style={box}
    >
      <source src={urls.webm} type="video/webm" />
      <source
        src={urls.mp4}
        type="video/mp4"
        onError={() => {
          missing(urls.mp4);
          setFailed(true);
        }}
      />
    </video>
  );
}

/**
 * The panel of a big figure (Survivor screen, Appearance, the encounter card): the dark gradient
 * and the glow, with the survivor in it. `animated` lets a Legendary outfit loop as a video of the
 * panel's full height, centred; the glow is then left out (the video has it). `className` sizes
 * the panel, `figureClassName` places the still picture in it; `dim` is a locked outfit (no glow).
 */
export function FigurePanel({
  bodyType,
  outfit = null,
  animated = false,
  alt = '',
  eager = false,
  size,
  dim = false,
  className,
  figureClassName = 'relative flex h-full w-full items-end justify-center',
  pictureClassName,
  children,
}: {
  bodyType: BodyType;
  outfit?: OutfitRef | null;
  animated?: boolean;
  alt?: string;
  eager?: boolean;
  size?: 512 | 1024;
  dim?: boolean;
  className?: string;
  figureClassName?: string;
  pictureClassName?: string;
  children?: ReactNode;
}) {
  const still = useStillOnly();
  // 11. 10. 2026: a Legendary whose loop is not made yet (`OUTFIT_ANIM_PENDING`) stays a still picture.
  const video = !!outfit && animated && outfit.rarity === 'legendary' && outfitAnimated(bodyType, outfit.set) && !still;
  return (
    <div data-figure-panel="" className={cx('relative isolate overflow-hidden', className)} style={{ background: PANEL_BG }}>
      {!video && !dim && (
        <span aria-hidden className="pointer-events-none absolute rounded-full" style={{ left: '50%', top: '38%', width: 'min(180px, 90%)', aspectRatio: '1', transform: 'translate(-50%, -50%)', background: FIGURE_GLOW }} />
      )}
      {video ? (
        <OutfitVideo key={`${bodyType}:${outfit!.set}`} bodyType={bodyType} set={outfit!.set} alt={alt} className="absolute inset-y-0 left-1/2 -translate-x-1/2" />
      ) : (
        <div className={figureClassName}>
          <CharacterArt bodyType={bodyType} outfit={outfit} alt={alt} eager={eager} size={size} className={pictureClassName} />
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * The whole survivor, standing, as a still picture. Fills the height of its box and keeps the 2:3
 * ratio. `outfit` shows that outfit (its transparent picture, Legendary too) instead of the base look.
 */
export function CharacterArt({
  bodyType,
  outfit = null,
  alt = '',
  className,
  eager = false,
  size,
}: {
  bodyType: BodyType;
  outfit?: OutfitRef | null;
  alt?: string;
  className?: string;
  eager?: boolean;
  size?: 512 | 1024;
}) {
  const key = outfit ? `${bodyType}:${outfit.set}:${outfit.rarity}` : null;
  const [broken, setBroken] = useState<string | null>(null);
  const show = outfit && broken !== key ? outfit : null;
  const src = (s: 512 | 1024) => (show ? outfitUrl(bodyType, show, s) : characterImage(bodyType, s));
  const img = (
    <img
      key={key ?? bodyType}
      src={src(size ?? 512)}
      alt={alt}
      width={688}
      height={1024}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable={false}
      data-outfit={show ? `${show.set}:${show.rarity}` : 'base'}
      onError={
        show
          ? (e) => {
              missing(e.currentTarget.currentSrc || e.currentTarget.src);
              setBroken(key);
            }
          : undefined
      }
      className={cx(FIGURE_CLS, className)}
      style={{ aspectRatio: CHARACTER_ASPECT }}
    />
  );
  if (size) return img;
  return (
    <picture className="contents">
      <source media={DESKTOP} srcSet={src(1024)} />
      {img}
    </picture>
  );
}

/** Head-and-shoulders portrait (the outfit's when one is worn); the rendered size comes from `className`. Other players' portraits load lazily. */
export function Portrait({ bodyType, outfit = null, size = 128, alt = '', className, lazy = true }: { bodyType: BodyType; outfit?: OutfitRef | null; size?: PortraitSize; alt?: string; className?: string; lazy?: boolean }) {
  const key = outfit ? `${bodyType}:${outfit.set}:${outfit.rarity}` : null;
  const [broken, setBroken] = useState<string | null>(null);
  const show = outfit && broken !== key ? outfit : null;
  const src = show ? outfitPortraitUrl(bodyType, show, size) : characterPortrait(bodyType, size);
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      width={size}
      height={size}
      loading={lazy ? 'lazy' : 'eager'}
      decoding="async"
      draggable={false}
      onError={
        show
          ? () => {
              missing(src);
              setBroken(key);
            }
          : undefined
      }
      className={cx('block aspect-square select-none rounded-full border border-border bg-raised object-cover', className)}
    />
  );
}

const preloaded = new Set<string>();

/** Preloads the player's own pictures (8.3), the worn outfit's when there is one; everyone else's load lazily when shown. */
export function preloadOwnLook(bodyType: BodyType | null | undefined, outfit?: OutfitRef | null) {
  if (!bodyType || typeof document === 'undefined') return;
  const add = (href: string, media?: string) => {
    if (preloaded.has(href)) return;
    preloaded.add(href);
    const l = document.createElement('link');
    l.rel = 'preload';
    l.as = 'image';
    l.href = href;
    if (media) l.media = media;
    document.head.appendChild(l);
  };
  const portrait = (s: PortraitSize) => (outfit ? outfitPortraitUrl(bodyType, outfit, s) : characterPortrait(bodyType, s));
  const figure = (s: 512 | 1024) => (outfit ? outfitUrl(bodyType, outfit, s) : characterImage(bodyType, s));
  add(portrait(128));
  add(portrait(256));
  add(figure(1024), DESKTOP);
  add(figure(512), '(max-width: 1023.98px)');
}

/**
 * Face of another survivor (plan v0.6, B2). NPC survivors and their ghosts get one of the 12
 * NPC portraits, keyed by their id, so the same NPC looks the same in the encounter, the report
 * and the horde; real players and their ghosts keep their own look, outfit included.
 */
export function OppFace({ opp, size = 128, alt = '', className }: { opp: { kind?: string; ghostId?: string | null; name: string; bodyType?: BodyType | null; outfit?: OutfitRef | null }; size?: PortraitSize; alt?: string; className?: string }) {
  const own = <Portrait bodyType={bodyTypeOf(opp.bodyType, opp.name)} outfit={opp.outfit} size={size} alt={alt} className={className} />;
  const npc = oppPortrait({ ...opp, bodyType: bodyTypeOf(opp.bodyType, opp.name) });
  return npc ? <NpcPortrait name={npc} size={size} alt={alt} className={className} fallback={own} /> : own;
}
